// @flow
import React, { Fragment, } from 'react';
import gql from 'graphql-tag';
import { FelaComponent, } from 'react-fela';
import { parseStyleProps, borderBottom, } from '@haaretz/htz-css-tools';
import { Query, GridItem, TdComponent, } from '@haaretz/htz-components';

import type { Node, } from 'react';
import type { DocumentNode, } from 'graphql/language/ast';
import type { StyleProps, ComponentPropResponsiveObject, } from '@haaretz/htz-css-tools';
import type { Asset, } from '@haaretz/htz-components';

import RowItem from '../../RowItem/RowItem';

type Field = {
  name: string, // eslint-disable-line react/no-unused-prop-types
  display?: string, // eslint-disable-line react/no-unused-prop-types
  fields?: Array<Field>,
  type?: string, // eslint-disable-line react/no-unused-prop-types
};

type TableProps = {
  id: string,
  fields: Array<Field>,
  tradingStatus?: boolean,
  fixed?: boolean,
  miscStyles?: StyleProps,
};

type TrComponentProps = {
  title: string,
  value: string | number,
  type?: string,
  miscStyles?: ?StyleProps,
};

type DisplayBodyProps = {
  fields: Array<Field>,
  assets: Array<Asset>,
};

const getFields: (Array<Field>) => string = fields => fields
  .map(({ name, display, type, fields, }: Field) => (fields ? `${name}\n { ${getFields(fields)} }` : `${name}\n`)
  )
  .join('\n');

function DisplayBody({ fields, assets, }: DisplayBodyProps): Node {
  return (
    <Fragment>
      {fields.map(({ display, name, type, fields, }: Field) => (fields ? (
        <DisplayBody fields={fields} assets={assets[0][name]} />
      ) : (
        <TrComponent key={name} title={display || ''} value={assets[0][name]} type={type} />
      ))
      )}
    </Fragment>
  );
}

const TradeStatsQuery = (fields: Array<Field>, tradingStatus?: boolean): DocumentNode => gql`
  query TradeStatsTable($assetsId: [String!]!){
    assets(ids: $assetsId){
      ${tradingStatus ? 'tradingStatus\n' : ''}
      ${getFields(fields)}
    }
  }
`;
const numToString: number => string = num => num.toLocaleString('he', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function TrComponent({ miscStyles, title, value, type, }: TrComponentProps): Node {
  return (
    <FelaComponent
      style={({ theme, }) => ({
        backgroundColor: theme.color('neutral', '-10'),
        extend: [
          borderBottom('2px', 1, 'solid', theme.color('neutral', '-6')),
          ...(miscStyles ? parseStyleProps(miscStyles, theme.mq, theme.type) : []),
        ],
      })}
      as="tr"
    >
      <TdComponent
        miscStyles={{
          paddingStart: '2rem',
          fontWeight: '700',
          width: '50%',
        }}
      >
        {title}
      </TdComponent>
      <TdComponent
        miscStyles={{
          paddingStart: '2rem',
          paddingEnd: '2rem',
          direction: 'ltr',
          textAlign: 'start',
        }}
      >
        {type && type === 'date'
          ? new Date(value).toLocaleString('it-It', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          })
          : typeof value === 'number'
            ? numToString(value)
            : value}
      </TdComponent>
    </FelaComponent>
  );
}

TrComponent.defaultProps = {
  type: null,
  miscStyles: null,
};

function QuoteInfoTable({ id, fields, tradingStatus, fixed, miscStyles, }: TableProps): Node {
  return (
    <Query query={TradeStatsQuery(fields, tradingStatus)} variables={{ assetsId: [ id, ], }}>
      {({ error, loading, data, }) => {
        if (error) return null;
        if (loading) return null;
        const { assets = [], } = data || {};
        return (
          <FelaComponent
            style={{
              flexGrow: '1',
            }}
          >
            <FelaComponent
              style={({ theme, }) => ({
                ...theme.type(-2),
                ...(fixed ? { tableLayout: 'fixed', } : {}),
                // whiteSpace: 'nowrap',
                width: '100%',
                extend: [ ...(miscStyles ? parseStyleProps(miscStyles, theme.mq, theme.type) : []), ],
              })}
              as="table"
            >
              {tradingStatus ? (
                <FelaComponent
                  style={({ theme, }) => ({
                    color: theme.color('neutral', '-3'),
                    marginBottom: '1rem',
                    marginTop: '1rem',
                    textAlign: 'start',
                  })}
                  as="caption"
                >
                  <FelaComponent
                    as="span"
                    style={{
                      ':after': {
                        content: '": "',
                      },
                    }}
                  >
                    שלב מסחר
                  </FelaComponent>
                  {assets[0].tradingStatus}
                </FelaComponent>
              ) : null}
              <tbody>
                <DisplayBody fields={fields} assets={assets} />
              </tbody>
            </FelaComponent>
          </FelaComponent>
        );
      }}
    </Query>
  );
}

QuoteInfoTable.defaultProps = {
  tradingStatus: false,
  fixed: false,
  miscStyles: null,
};

type Props = TableProps & {
  width: number | ComponentPropResponsiveObject<number>[],
  title?: string,
  gutter?: number,
  rowMiscStyles?: StyleProps,
};

export default ({ width, title, gutter, rowMiscStyles, ...props }: Props) => (
  <GridItem
    width={width}
    gutter={gutter || 2}
    miscStyles={{
      display: 'flex',
      flexDirection: 'column',
    }}
  >
    {title ? (
      <RowItem title={title} miscStyles={rowMiscStyles}>
        <QuoteInfoTable {...props} />
      </RowItem>
    ) : (
      <QuoteInfoTable {...props} />
    )}
  </GridItem>
);
